import { useState, useEffect, useCallback } from 'react'
import {
    FileText, Volume2, Image, Shield, CheckCircle, XCircle,
    ChevronDown, ChevronUp, Download, Search, Loader2, AlertCircle,
    FileAudio, Eye
} from 'lucide-react'
import Cookies from 'js-cookie'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

function getAuthHeaders(): HeadersInit {
    const token = Cookies.get('token')
    return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// ─── Document-slot definitions ───────────────────────────────────────────────
interface DocSlot {
    key: string
    label: string
    category: 'PDF' | 'Audio' | 'ID' | 'Selfie'
    type: 'Initial' | 'Manual'
    accept: string
    icon: 'pdf' | 'audio' | 'image'
}

const DOC_SLOTS: DocSlot[] = [
    // PDFs
    { key: 'initialCmsConsentPdf', label: 'CMS Written Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    { key: 'initialCarrierConsentPdf', label: 'Carrier Written Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    { key: 'initialRenewalConsentPdf', label: 'Next-Year Renewal Consent', category: 'PDF', type: 'Initial', accept: '.pdf', icon: 'pdf' },
    // Audio
    { key: 'initialCmsAudio', label: 'CMS Voice Consent', category: 'Audio', type: 'Initial', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'initialCarrierAudio', label: 'Carrier Voice Consent', category: 'Audio', type: 'Initial', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'manualCmsAudio', label: 'CMS Voice Consent (Manual Upload)', category: 'Audio', type: 'Manual', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    { key: 'manualCarrierAudio', label: 'Carrier Voice Consent (Manual Upload)', category: 'Audio', type: 'Manual', accept: '.mp3,.wav,audio/*', icon: 'audio' },
    // ID Uploads
    { key: 'initialIdUpload', label: 'Client ID Photo', category: 'ID', type: 'Initial', accept: 'image/*,.pdf', icon: 'image' },
    { key: 'manualIdUpload', label: 'Client ID Photo (Manual Upload)', category: 'ID', type: 'Manual', accept: 'image/*,.pdf', icon: 'image' },
    // Selfies
    { key: 'initialSelfie', label: 'Client Selfie', category: 'Selfie', type: 'Initial', accept: 'image/*', icon: 'image' },
    { key: 'manualSelfie', label: 'Client Selfie (Manual Upload)', category: 'Selfie', type: 'Manual', accept: 'image/*', icon: 'image' },
]

const CATEGORY_COLORS: Record<string, string> = {
    PDF: 'bg-red-100 text-red-700 border-red-200',
    Audio: 'bg-purple-100 text-purple-700 border-purple-200',
    ID: 'bg-blue-100 text-blue-700 border-blue-200',
    Selfie: 'bg-green-100 text-green-700 border-green-200'
}

function DocSlotIcon({ icon }: { icon: string }) {
    if (icon === 'pdf') return <FileText className="w-4 h-4" />
    if (icon === 'audio') return <FileAudio className="w-4 h-4" />
    return <Image className="w-4 h-4" />
}

// ─── ComplianceDocsPanel — expanded view for one session ────────────────────
function ComplianceDocsPanel({ sessionId }: { sessionId: string }) {
    const [detail, setDetail] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [uploading, setUploading] = useState<string | null>(null)
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null)

    useEffect(() => {
        fetch(`${API_BASE_URL}/application-data-logs/${sessionId}`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(data => { if (data.success) setDetail(data.data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [sessionId])

    const handleFileUpload = async (slotKey: string, file: File) => {
        setUploading(slotKey)
        try {
            const base64 = await new Promise<string>((res, rej) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => res(reader.result as string)
                reader.onerror = rej
            })

            const resp = await fetch(`${API_BASE_URL}/application-data-logs/${sessionId}/upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ documentType: slotKey, fileBase64: base64, fileName: file.name })
            })
            const result = await resp.json()
            if (result.success) {
                setNotification({ type: 'success', message: `${slotKey} uploaded successfully!` })
                // Refresh detail
                const updated = await fetch(`${API_BASE_URL}/application-data-logs/${sessionId}`, { headers: getAuthHeaders() })
                const updatedData = await updated.json()
                if (updatedData.success) setDetail(updatedData.data)
            } else {
                setNotification({ type: 'error', message: result.error || 'Upload failed' })
            }
        } catch (e: any) {
            console.error('Upload failed:', e)
            setNotification({ type: 'error', message: e.message || 'Upload failed' })
        } finally {
            setUploading(null)
            setTimeout(() => setNotification(null), 3000)
        }
    }

    if (loading) return (
        <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
            <span className="text-gray-600">Loading compliance documents...</span>
        </div>
    )

    if (!detail) return (
        <div className="text-center py-8 text-gray-500">Failed to load document details.</div>
    )

    const docs = detail.documents || {}

    // Group by category
    const groups = ['PDF', 'Audio', 'ID', 'Selfie'] as const
    const slotsByCategory: Record<string, DocSlot[]> = {}
    groups.forEach(g => { slotsByCategory[g] = DOC_SLOTS.filter(s => s.category === g) })

    return (
        <div className="bg-gray-50 border-t border-gray-200 p-6">
            {notification && (
                <div className={`mb-4 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium animate-in fade-in slide-in-from-top-2 ${notification.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {notification.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {notification.message}
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {groups.map(group => (
                    <div key={group} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className={`px-4 py-3 border-b font-semibold text-sm ${CATEGORY_COLORS[group]}`}>
                            {group === 'PDF' && '📄 Consent PDF Forms'}
                            {group === 'Audio' && '🎙️ Voice Consent Audio'}
                            {group === 'ID' && '🪪 ID Documents'}
                            {group === 'Selfie' && '📸 Client Selfie Photos'}
                        </div>
                        <div className="divide-y divide-gray-100">
                            {slotsByCategory[group].map(slot => {
                                const docData = docs[slot.key]
                                const hasFile = !!docData?.url
                                const isUploading = uploading === slot.key

                                return (
                                    <div key={slot.key} className="px-4 py-3 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className={`p-1.5 rounded ${hasFile ? 'text-green-600 bg-green-50' : 'text-gray-400 bg-gray-100'}`}>
                                                <DocSlotIcon icon={slot.icon} />
                                            </span>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-gray-800 truncate">{slot.label}</div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-xs px-1.5 py-0.5 rounded border ${slot.type === 'Initial' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-orange-50 text-orange-600 border-orange-200'}`}>
                                                        {slot.type}
                                                    </span>
                                                    {docData?.uploadedAt && (
                                                        <span className="text-xs text-gray-400">
                                                            {new Date(docData.uploadedAt).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {hasFile ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                    {slot.icon === 'audio' ? (
                                                        <audio controls src={docData.url} className="h-7 w-32" />
                                                    ) : (
                                                        <a
                                                            href={docData.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" /> View
                                                        </a>
                                                    )}
                                                    <a href={docData.url} download className="text-gray-500 hover:text-gray-700">
                                                        <Download className="w-3.5 h-3.5" />
                                                    </a>
                                                </>
                                            ) : (
                                                <div className="flex items-center gap-1.5">
                                                    <XCircle className="w-4 h-4 text-red-400" />
                                                    <label className={`cursor-pointer text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center gap-1 ${isUploading ? 'opacity-60 cursor-wait' : ''}`}>
                                                        {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 rotate-180" />}
                                                        Upload
                                                        <input
                                                            type="file"
                                                            className="sr-only"
                                                            accept={slot.accept}
                                                            disabled={isUploading}
                                                            onChange={e => {
                                                                const f = e.target.files?.[0]
                                                                if (f) handleFileUpload(slot.key, f)
                                                            }}
                                                        />
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ApplicationDataLogs() {
    const [applications, setApplications] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)
    const [expandedRow, setExpandedRow] = useState<string | null>(null)

    const fetchData = useCallback(async (searchVal: string, pageVal: number) => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams({
                page: String(pageVal),
                limit: '15',
                ...(searchVal ? { search: searchVal } : {})
            })
            const resp = await fetch(`${API_BASE_URL}/application-data-logs?${params}`, {
                headers: getAuthHeaders()
            })
            if (!resp.ok) throw new Error('Failed to load')
            const data = await resp.json()
            if (data.success) {
                setApplications(data.data)
                setTotalPages(data.pagination.totalPages)
                setTotal(data.pagination.total)
            }
        } catch (e: any) {
            setError(e.message || 'Failed to load application data logs')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => fetchData(search, page), 300)
        return () => clearTimeout(timer)
    }, [search, page, fetchData])

    const completionColor = (count: number) => {
        if (count === 11) return 'text-green-600 bg-green-50'
        if (count >= 6) return 'text-yellow-600 bg-yellow-50'
        if (count >= 1) return 'text-orange-600 bg-orange-50'
        return 'text-red-600 bg-red-50'
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6 p-6">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Application Data Logs</h1>
                        <p className="text-sm text-gray-500">Compliance document vault for all submitted applications</p>
                    </div>
                </div>

                {/* Stats banner */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-indigo-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-indigo-700">{total}</div>
                        <div className="text-xs text-indigo-600 font-medium">Total Applications</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-green-700">
                            {applications.filter(a => a.documentsComplete === 11).length}
                        </div>
                        <div className="text-xs text-green-600 font-medium">Fully Complete (11/11)</div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-red-700">
                            {applications.filter(a => a.documentsComplete < 11).length}
                        </div>
                        <div className="text-xs text-red-600 font-medium">Incomplete Documents</div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 px-4 py-3 flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                    type="text"
                    placeholder="Search by client name or agent name..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1) }}
                    className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
                />
                {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                    <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <div className="col-span-3">Client</div>
                        <div className="col-span-2">Agent</div>
                        <div className="col-span-2">Campaign</div>
                        <div className="col-span-2">Submitted</div>
                        <div className="col-span-2 text-center">Documents (11 required)</div>
                        <div className="col-span-1"></div>
                    </div>
                </div>

                {error ? (
                    <div className="flex items-center gap-3 p-8 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span>{error}</span>
                    </div>
                ) : loading && applications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
                        <p className="mt-4 text-gray-500 font-medium">Fetching application logs...</p>
                    </div>
                ) : applications.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No completed applications found</p>
                        {search && <p className="text-sm mt-1">Try a different search term</p>}
                    </div>
                ) : (
                    <div className={`divide-y divide-gray-100 ${loading ? 'opacity-50' : ''}`}>
                        {applications.map((app, idx) => {
                            const isExpanded = expandedRow === app.enrollmentSessionId
                            const status = app.complianceStatus || {}
                            const completedCount = app.documentsComplete || 0

                            return (
                                <div key={app.enrollmentSessionId}>
                                    <div
                                        className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => setExpandedRow(isExpanded ? null : app.enrollmentSessionId)}
                                    >
                                        <div className="grid grid-cols-12 gap-4 items-center">
                                            {/* Client */}
                                            <div className="col-span-3">
                                                <div className="font-semibold text-gray-900 text-sm">{app.clientName}</div>
                                                <div className="text-xs text-gray-400 truncate">{app.clientEmail}</div>
                                            </div>

                                            {/* Agent */}
                                            <div className="col-span-2 text-sm text-gray-700">{app.agentName}</div>

                                            {/* Campaign */}
                                            <div className="col-span-2">
                                                <div className="text-sm font-medium text-gray-900 truncate" title={app.campaignId}>
                                                    {app.campaignName}
                                                </div>
                                                <div className="text-[10px] text-gray-400 font-mono tracking-tighter">
                                                    {app.campaignId?.substring(0, 8)}...
                                                </div>
                                            </div>

                                            {/* Submitted */}
                                            <div className="col-span-2 text-xs text-gray-500">
                                                {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : '—'}
                                            </div>

                                            {/* Document completion */}
                                            <div className="col-span-2 flex flex-col items-center gap-1.5">
                                                <span className={`text-sm font-bold px-3 py-1 rounded-full ${completionColor(completedCount)}`}>
                                                    {completedCount}/11
                                                </span>
                                                {/* Mini dot grid */}
                                                <div className="flex flex-wrap gap-1 justify-center max-w-[88px]">
                                                    {Object.values(status).map((v: any, i: number) => (
                                                        <div key={i} className={`w-2 h-2 rounded-full ${v ? 'bg-green-500' : 'bg-red-300'}`} />
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Expand toggle */}
                                            <div className="col-span-1 flex justify-end">
                                                {isExpanded
                                                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                                                    : <ChevronDown className="w-4 h-4 text-gray-400" />
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded compliance panel */}
                                    {isExpanded && (
                                        <ComplianceDocsPanel sessionId={app.enrollmentSessionId} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing <span className="font-semibold text-gray-900">{(page - 1) * 15 + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * 15, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> results
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >Previous</button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >Next</button>
                    </div>
                </div>
            </div>
        </div>
    )
}
